import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';

import { ContactFeedback, PKUrl } from 'components';
import { Chains, ChainsBroken } from 'components/atoms/CustomSVGIcon';
import MODALS from 'constants/modals';
import contactDetail from 'features/views/contactDetail';
import contactList from 'features/views/contactList';
import contacts from 'features/views/contacts';
import user from 'features/views/user';
import { equalOrThreeDashes, getCodelistPropById, getRoutePath } from 'utils';
import { getClientName } from 'utils/client';

export const ContactsTableRow = ({
	id,
	contact = {},
	solutionStates,
	idkRegionsCodelist,
	idkAgenciesCodelist,
	isDetailVisible,
	onDetailVisibilityChange,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const selectedContactId = useSelector(contactList.selectors.getSelectedContactId);
	const isContactInSuccessfullState = useSelector((state) =>
		contactList.selectors.getIsContactInSuccessfullState(state, contact?.solutionStateId),
	);

	const {
		client = {},
		campaign = {},
		policy = {},
		solver = {},
		productName,
		solutionStateId,
		hasBrutto,
		solverName,
	} = contact;

	const { fullName, endDate, communicationCode } = campaign;
	const { policyNo, systemId } = policy;
	const { ccdId, personIn, businessIn } = client;

	const clientName = getClientName(client);
	const formattedSolverName = contactDetail.utils.getOwnerValueWithSolverName(
		solverName,
		solver,
		idkAgenciesCodelist,
		idkRegionsCodelist,
		'shortDescription',
	);

	const handleFeedbackSuccess = useCallback(
		(id) => {
			dispatch(user.actions.fetchAdditionalUserInfo());
			dispatch(contacts.actions.replaceContact(id));
		},
		[dispatch],
	);

	const handleSelectContact = useCallback(() => {
		dispatch(contactList.actions.selectContact(id));
	}, [dispatch, id]);

	const handleOpenBruttoProductionModal = useCallback(() => {
		NiceModal.show(MODALS.brutto_production, { contact });
	}, [contact]);

	const handleToggleFeedback = useCallback(
		(e) => {
			e.stopPropagation();
			onDetailVisibilityChange();
		},
		[onDetailVisibilityChange],
	);

	const handleRedirectToDetail = useCallback(
		(contactId) => (event) => {
			event.preventDefault();
			event.stopPropagation();

			const { pathname, state } = getRoutePath('contactDetail', { contactId }, true);
			navigate(pathname, { state });
		},
		[navigate],
	);

	const generatedBruttoIcon = useMemo(() => {
		if (isContactInSuccessfullState) {
			if (hasBrutto) {
				return (
					<Tooltip title={getLabelMessage('contacts:bruttoProductionPaired')}>
						<IconButton disableRipple>
							<Chains
								style={{ color: 'green', fontSize: '20px' }}
								className="Link"
								onClick={handleOpenBruttoProductionModal}
							/>
						</IconButton>
					</Tooltip>
				);
			} else {
				return (
					<Tooltip title={getLabelMessage('contacts:bruttoProductionNotPaired')}>
						<IconButton disableRipple sx={{ cursor: 'default' }}>
							<ChainsBroken style={{ color: 'red', fontSize: '20px' }} />
						</IconButton>
					</Tooltip>
				);
			}
		} else {
			return null;
		}
	}, [hasBrutto, isContactInSuccessfullState, handleOpenBruttoProductionModal]);

	return (
		<>
			<TableRow
				sx={{
					'& > *': { borderBottom: 'unset' },
					backgroundColor: selectedContactId === id ? 'primary.shades.8p' : 'default',
				}}
				onClick={handleSelectContact}
			>
				<TableCell>
					<PKUrl.PKPartner
						partnerId={ccdId}
						campaignEndDate={endDate}
						communicationCode={communicationCode}
						unlock
						muiLink
					>
						{equalOrThreeDashes(clientName)}
					</PKUrl.PKPartner>
				</TableCell>
				<TableCell>{equalOrThreeDashes(fullName)}</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(productName)}</div>
					<PKUrl.PKPolicy
						partnerId={ccdId}
						policyNo={policyNo}
						systemId={systemId}
						campaignEndDate={endDate}
						communicationCode={communicationCode}
						unlock
						muiLink
					>
						{equalOrThreeDashes(policyNo)}
					</PKUrl.PKPolicy>
				</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(personIn)}</div>
					<div>{equalOrThreeDashes(businessIn)}</div>
				</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(formattedSolverName)}</div>
					<div>
						{equalOrThreeDashes(getCodelistPropById(solutionStateId, solutionStates, 'shortDescription'))}
					</div>
				</TableCell>
				<TableCell>{generatedBruttoIcon}</TableCell>
				<TableCell>
					<Link underline="none" onClick={handleRedirectToDetail(id)}>
						<Box sx={{ color: 'primary.main', fontWeight: 'bold' }}>{getLabelMessage('common:detail')}</Box>
					</Link>
				</TableCell>
				<TableCell>
					{isDetailVisible ? (
						<ChevronUp
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					) : (
						<ChevronDown
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					)}
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={9}>
					<Collapse in={isDetailVisible} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							<ContactFeedback contact={contact} onSuccess={handleFeedbackSuccess} />
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

ContactsTableRow.propTypes = {
	contact: PropTypes.object,
	id: PropTypes.number,
	idkAgenciesCodelist: PropTypes.arrayOf(PropTypes.object),
	idkRegionsCodelist: PropTypes.arrayOf(PropTypes.object),
	isDetailVisible: PropTypes.bool,
	onDetailVisibilityChange: PropTypes.func,
	solutionStates: PropTypes.arrayOf(PropTypes.object),
};

export default ContactsTableRow;
